import { inject, Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { endpoints } from '../../../environments/environment.common';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from './storage/local-storage.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
    private readonly swPush = inject(SwPush);
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly storage = inject(LocalStorageService);

    private get apiBase(): string {
        return environment.apiUrl;
    }

    private url(path: string): string {
        return `${this.apiBase}/${path.replace(/^\/+/, '')}`;
    }

    /** Returns true if the browser supports push notifications. */
    get isSupported(): boolean {
        return this.swPush.isEnabled;
    }

    /**
     * Request notification permission and subscribe to Web Push.
     * Safe to call multiple times — re-uses an existing subscription if present.
     */
    async init(): Promise<void> {
        if (!this.isSupported) {
            console.warn('[Push] Service worker not enabled (dev mode or HTTPS required).');
            return;
        }

        if (Notification.permission === 'denied') {
            console.warn('[Push] Notification permission denied by the user.');
            return;
        }

        try {
            const vapidPublicKey = await this.fetchVapidPublicKey();
            if (!vapidPublicKey) return;

            const sub = await this.swPush.requestSubscription({ serverPublicKey: vapidPublicKey });
            await this.sendSubscriptionToServer(sub);
        } catch (err) {
            console.error('[Push] Subscription failed:', err);
        }
    }

    async unsubscribe(): Promise<void> {
        if (!this.isSupported) return;

        try {
            const sub = await firstValueFrom(this.swPush.subscription);
            if (sub) {
                await this.http
                    .delete(this.url(endpoints.notifications.unsubscribe), {
                        body: { endpoint: sub.endpoint },
                        withCredentials: true,
                    })
                    .toPromise();
                await this.swPush.unsubscribe();
            }
        } catch (err) {
            console.error('[Push] Unsubscribe failed:', err);
        }
    }

    /** Listen to incoming silent push messages (e.g. REQUEST_LOCATION). */
    listenToMessages(): void {
        this.swPush.messages.subscribe((msg: any) => {
            if (msg?.type === 'REQUEST_LOCATION') {
                this.handleLocationRequest(msg);
            }
        });
    }

    /** Listen to notification click events and navigate to the booking. */
    listenToNotificationClicks(): void {
        this.swPush.notificationClicks.subscribe(({ action, notification }) => {
            const bookingId = notification.data?.booking_id;
            if (bookingId) {
                this.router.navigate(['/bookings', bookingId]);
            }
        });
    }

    // -----------------------------------------------------------------------

    private async fetchVapidPublicKey(): Promise<string | null> {
        try {
            const res: any = await firstValueFrom(
                this.http.get(this.url(endpoints.notifications.vapidPublicKey), { withCredentials: true })
            );
            return res?.data?.vapid_public_key ?? null;
        } catch {
            console.error('[Push] Could not fetch VAPID public key.');
            return null;
        }
    }

    private async sendSubscriptionToServer(sub: PushSubscription): Promise<void> {
        const key = sub.getKey('p256dh');
        const auth = sub.getKey('auth');

        if (!key || !auth) return;

        const payload = {
            endpoint:   sub.endpoint,
            public_key: btoa(String.fromCharCode(...new Uint8Array(key))),
            auth_token: btoa(String.fromCharCode(...new Uint8Array(auth))),
        };

        await firstValueFrom(
            this.http.post(this.url(endpoints.notifications.subscribe), payload, { withCredentials: true })
        );
    }

    private handleLocationRequest(msg: any): void {
        const driverId = this.storage.getItem('driver_id');
        if (!driverId || !navigator.geolocation) return;

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                this.http
                    .post(
                        this.url(`/api/v1/drivers/${driverId}/location`),
                        { lat: pos.coords.latitude, lng: pos.coords.longitude },
                        { withCredentials: true }
                    )
                    .subscribe({ error: (e) => console.error('[Push] Location POST failed', e) });
            },
            (err) => console.warn('[Push] Geolocation denied:', err)
        );
    }
}
